import { GgcBadge } from '@/components/ui/ggc/badge';
import { useI18nText } from '@/lib/utils/i18nText';

/**
 * Where a tag autocomplete suggestion comes from:
 * - `campaign` / `roster`: matches a campaign or roster name (rosters are
 *   campaigns with status === 'roster'; the BE syncs each name as a tag value)
 * - `recent`: a plain previously-used tag with no campaign/roster of that name
 */
export type TagKind = 'campaign' | 'roster' | 'recent';

export interface TagSuggestion {
  value: string;
  kind: TagKind;
}

/** Minimal campaign shape needed to classify tag suggestions. */
export interface TagCampaignLike {
  _id: string;
  id?: string;
  name?: string;
  status?: string;
}

/**
 * Map every campaign/roster name to its kind so a raw tag string can be
 * classified as a campaign name, a roster name, or (absent) a plain tag.
 */
export function buildTagKindMap(campaigns: TagCampaignLike[]): Map<string, 'campaign' | 'roster'> {
  const map = new Map<string, 'campaign' | 'roster'>();
  for (const c of campaigns) {
    const name = c.name?.trim();
    if (!name || map.has(name)) continue;
    map.set(name, c.status === 'roster' ? 'roster' : 'campaign');
  }
  return map;
}

const EMPTY_SET: ReadonlySet<string> = new Set();

/**
 * Build the ordered, classified tag autocomplete list shared by the dashboard
 * Tag filter and the Edit-tags dialog.
 * - typing: campaign/roster name matches first (own campaigns before others),
 *   then other previously-used tags — all filtered by `query`
 * - empty: recently-used tags, then all previously-used tags
 * Every entry carries its `kind` so the UI can prefix a category chip.
 */
export function buildTagSuggestions({
  query,
  kindMap,
  ownNames,
  recentTags,
  oldTags,
  exclude,
  limit,
}: {
  query: string;
  kindMap: Map<string, 'campaign' | 'roster'>;
  ownNames?: ReadonlySet<string>;
  recentTags: string[];
  oldTags: string[];
  exclude: string[];
  limit: number;
}): TagSuggestion[] {
  const excluded = new Set(exclude.map((s) => s.trim()));
  const seen = new Set<string>();
  const out: TagSuggestion[] = [];
  const q = query.trim().toLowerCase();

  const push = (value: string) => {
    if (out.length >= limit) return;
    const v = value.trim();
    if (!v || seen.has(v) || excluded.has(v)) return;
    if (q && !v.toLowerCase().includes(q)) return;
    seen.add(v);
    out.push({ value: v, kind: kindMap.get(v) ?? 'recent' });
  };

  if (q) {
    const own = ownNames ?? EMPTY_SET;
    // campaign/roster names first — the influencer's own ones ahead of the rest
    for (const name of kindMap.keys()) if (own.has(name)) push(name);
    for (const name of kindMap.keys()) if (!own.has(name)) push(name);
  }
  // Empty focus intentionally does NOT enumerate the full kindMap (hundreds of
  // unrelated campaign/roster names would bury the useful picks) — it just shows
  // recently-used then previously-used tags, each still classified/chipped by
  // kindMap. Campaign/roster names surface once the user starts typing.
  for (const tag of recentTags) push(tag);
  for (const tag of oldTags) push(tag);
  return out;
}

const KIND_STATUS = {
  campaign: 'active',
  roster: 'roster',
  recent: 'muted',
} as const;

/** Leading category chip shown in a tag autocomplete row. */
export function TagKindChip({ kind }: { kind: TagKind }) {
  const { t } = useI18nText();
  const label =
    kind === 'campaign'
      ? t('Campaign', '캠페인')
      : kind === 'roster'
        ? t('Roster', '로스터')
        : t('Recent', '이전');
  return (
    <GgcBadge
      status={KIND_STATUS[kind]}
      hideDot
      className="shrink-0 px-1.5 py-0 text-[8px] leading-[15px]"
    >
      {label}
    </GgcBadge>
  );
}
